<template>
  <aside v-if="showExpiredCardToast" id="paymentToast" class="payment-toast">
    <div class="payment-toast__wrapper payment-toast__type2">
      <h3>Tu tarjeta está a punto de caducar</h3>
      <p>
        Tu tarjeta caduca el <strong>{{ expiredCardDate }}</strong>.
        Actualiza tus datos de pago para seguir disfrutando de tu suscripción a infoLibre.
        La información que recibes depende de ti.
      </p>
      <button @click.prevent="clickExpiredCard">Actualizar datos de pago <i class="fal fa-credit-card"></i></button>
    </div>
    <button @click.prevent="clickCloseExpiredCardToast" class="paymentToastClose fal fa-times"></button>
    <span class="narrow"></span>
  </aside>

  <aside v-else-if="showShoppingCartToast" id="paymentToast" class="payment-toast">
    <div class="payment-toast__wrapper payment-toast__type1">
      <h3>¡Termina tu compra!</h3>
      <p>
        Parece que tuviste problemas para completar la suscripción a infoLibre.
        Finaliza el proceso en sólo unos minutos y apóyanos.
        La información que recibes depende de ti.
      </p>
      <button
        @click.prevent="clickShoppingCart"
        :data-dl-subscription-params="extraEventShoppingCart"
      >
        Finalizar suscripción <i class="fal fa-shopping-cart"></i>
      </button>
    </div>
    <button @click.prevent="clickCloseShoppingCartToast" class="paymentToastClose fal fa-times"></button>
    <span class="narrow"></span>
  </aside>

  <aside v-else-if="showGracePeriodToast" id="paymentToast" class="payment-toast">
    <div class="payment-toast__wrapper payment-toast__type3">
      <h3>No podemos cobrar tu cuota</h3>
      <p>
        Tus datos de pago son erróneos o incompletos.
        Actualiza tu información para renovar la cuota y evitar la baja de tu suscripción a infoLibre.
      </p>
      <button @click.prevent="clickGracePeriod">Actualizar datos de pago <i class="fal fa-pencil"></i></button>
    </div>
    <button @click.prevent="clickCloseGracePeriodToast" class="paymentToastClose fal fa-times"></button>
    <span class="narrow"></span>
  </aside>
</template>

<script>
import { mapGetters } from 'vuex';
import Cookies from 'js-cookie';
import { getDefaultCookiePaymentToastExpires } from '@/functions';
import {
  PAYMENT_TOAST_SHOWED_EXPIRED_CARD_COOKIE_NAME,
  PAYMENT_TOAST_SHOWED_GRACE_PERIOD_COOKIE_NAME,
  PAYMENT_TOAST_SHOWED_SHOPPING_CART_COOKIE_NAME,
  MILLISECONDS_IN_A_DAY,
} from '@/config';

const PAYMENT_TOAST_EXPIRED_CARD = 1;
const PAYMENT_TOAST_SHOPPING_CART = 2;
const PAYMENT_TOAST_GRACE_PERIOD = 3;

export default {
  name: 'edi-payment-toast',
  data() {
    return {
      type: 0,
    };
  },
  props: {
    expiredCardUrl: String,
    shoppingCartUrl: String,
    gracePeriodUrl: String,
  },
  computed: {
    ...mapGetters([
      'isLogged',
      'shoppingCart',
      'expiredCard',
      'gracePeriod',
    ]),

    expiredCardDate() {
      if (this.type !== PAYMENT_TOAST_EXPIRED_CARD) {
        return '';
      }

      const date = new Date(this.expiredCard);
      const year = date.getFullYear();
      const month = (`${date.getMonth() + 1}`).padStart(2, '0');
      const day = (`${date.getDate()}`).padStart(2, '0');
      return `${day}/${month}/${year}`;
    },
    showExpiredCardToast() {
      return this.type === PAYMENT_TOAST_EXPIRED_CARD;
    },
    extraEventShoppingCart() {
      return JSON.stringify({
        im_location: 'finalizar_suscripcion',
        im_type: 'infoLibre',
      });
    },
    showShoppingCartToast() {
      return this.type === PAYMENT_TOAST_SHOPPING_CART;
    },
    showGracePeriodToast() {
      return this.type === PAYMENT_TOAST_GRACE_PERIOD;
    },
  },
  methods: {
    addStackDataLayerEvents(imType) {
      window.stackDataLayerEvents = window.stackDataLayerEvents || [];
      window.stackDataLayerEvents.push({
        priority: 0,
        data: {
          event: 'notification_alert',
          eventData: {
            im_type: imType,
          },
        },
      });
    },
    sendDataLayerEvent(eventName, imType) {
      window.dataLayer.push({
        event: eventName,
        eventData: {
          im_type: imType,
        },
      });
    },
    clickExpiredCard() {
      this.sendDataLayerEvent('click_notification_alert', 'actualizar_datos_pago');
      window.open(this.expiredCardUrl, '_blank');
    },
    clickShoppingCart() {
      this.sendDataLayerEvent('click_notification_alert', 'finalizar_suscripcion');
      window.open(this.shoppingCartUrl, '_blank');
    },
    clickGracePeriod() {
      this.sendDataLayerEvent('click_notification_alert', 'actualizar_datos');
      window.open(this.gracePeriodUrl, '_blank');
    },
    toastCloseEvents(cookieName, expires) {
      this.type = 0;
      Cookies.set(cookieName, 1, { expires });
    },
    clickCloseShoppingCartToast() {
      this.toastCloseEvents(PAYMENT_TOAST_SHOWED_SHOPPING_CART_COOKIE_NAME, getDefaultCookiePaymentToastExpires());
    },
    clickCloseExpiredCardToast() {
      this.toastCloseEvents(PAYMENT_TOAST_SHOWED_EXPIRED_CARD_COOKIE_NAME, getDefaultCookiePaymentToastExpires());
    },
    clickCloseGracePeriodToast() {
      this.toastCloseEvents(PAYMENT_TOAST_SHOWED_GRACE_PERIOD_COOKIE_NAME, getDefaultCookiePaymentToastExpires());
    },
  },
  mounted() {
    if (!this.isLogged) {
      return;
    }

    if (this.shoppingCart !== undefined) {
      if (Cookies.get(PAYMENT_TOAST_SHOWED_SHOPPING_CART_COOKIE_NAME) !== undefined) {
        return;
      }

      // Si han pasado más de 6 días desde que abandonó el carrito, no lo mostramos
      const date = new Date(this.shoppingCart);
      const now = new Date();
      const daysDiff = (now - date) / MILLISECONDS_IN_A_DAY;
      if (daysDiff > 6) {
        return;
      }

      this.type = PAYMENT_TOAST_SHOPPING_CART;
      this.addStackDataLayerEvents('finalizar_suscripcion');
    } else if (this.expiredCard !== undefined) {
      if (Cookies.get(PAYMENT_TOAST_SHOWED_EXPIRED_CARD_COOKIE_NAME) !== undefined) {
        return;
      }

      this.type = PAYMENT_TOAST_EXPIRED_CARD;
      this.addStackDataLayerEvents('actualizar_datos_pago');
    } else if (this.gracePeriod !== undefined) {
      if (Cookies.get(PAYMENT_TOAST_SHOWED_GRACE_PERIOD_COOKIE_NAME) !== undefined) {
        return;
      }

      this.type = PAYMENT_TOAST_GRACE_PERIOD;
      this.addStackDataLayerEvents('actualizar_datos');
    }
  },
};
</script>
